// Class representing the color chip functionality for product items.
class ColorChipManager {
  // Create a ColorChipManager.
  constructor() {
    this.productItems = Array.from(document.querySelectorAll('.product-item'));
    this.colorSwatchContainers = Array.from(document.querySelectorAll('.product-item__swatch'));
    this.initFacetChangeListener();
  }

  // Show carousels.
  showCarousels() {
    for (let i = 0; i < this.productItems.length; i++) {
      const flickityElement = this.productItems[i].querySelector('flickity-carousel');
      if (flickityElement) {
        requestAnimationFrame(() => {
          flickityElement.style.opacity = '1';
        });
      }
    }
  }

  // Initialize the color chip functionality.
  init() {
    this.productItems.forEach(productItem => {
      const flickityElement = productItem.querySelector('flickity-carousel');
      if (flickityElement) {
        flickityElement.removeEventListener('flickity:ready', this._preloadNextImage);
        flickityElement.removeEventListener('flickity:slide-changed', this._preloadNextImage);
      }
    });
    this.initProductItems();
    this.initColorSwatchContainers();
    this.initialFilterImages();
  }

  // Initialize product items.
  initProductItems() {
    this.productItems.forEach(productItem => {
      const colorChips = productItem.querySelectorAll('.product-item__list-item');
      const flickityElement = productItem.querySelector('flickity-carousel');
      if (flickityElement) {
        this.observeFlickityElement(colorChips, flickityElement, productItem);
      }
    });
  }

  // Observe Flickity element for changes.
  observeFlickityElement(colorChips, flickityElement, productItem, initialUrl = null) {
    const observer = new MutationObserver(async (mutations, obs) => {
      const flickityInstance = await this.getFlickityInstance(flickityElement);
      if (flickityInstance) {
        obs.disconnect();
        if (initialUrl) {
          await this.filterImages(flickityElement, flickityInstance, initialUrl);
          requestAnimationFrame(() => {
            flickityElement.style.opacity = '1';
          });
        }
        this.setupColorChips(colorChips, flickityElement, flickityInstance, productItem);
        this.setupArrowState(flickityInstance, flickityElement); // Setup arrow state
      }
    });
    observer.observe(flickityElement, {
      attributes: true,
      childList: true,
      subtree: true
    });
  }

  // Set up arrow state for Flickity instance
  setupArrowState(flickityInstance, flickityElement) {
    const prevButton = flickityElement.querySelector('.flickity-button.previous');
    const nextButton = flickityElement.querySelector('.flickity-button.next');
    if (!prevButton || !nextButton) return;
    const updateArrowState = () => {
      const currentIndex = flickityInstance.selectedIndex;
      const lastIndex = flickityInstance.slides.length - 1;
      prevButton.style.opacity = currentIndex === 0 ? 0.25 : 1;
      nextButton.style.opacity = currentIndex === lastIndex ? 0.25 : 1;
    };

    // Attach the updateArrowState function to Flickity's 'change' event
    flickityInstance.on('change', updateArrowState);

    // Initialize the arrow state on load
    updateArrowState();
  }

  // Initialize color swatch containers.
  initColorSwatchContainers() {
    this.colorSwatchContainers.forEach(container => {
      new ColorSwatchSlider(container).init();
    });
  }

  // Get Flickity instance.
  async getFlickityInstance(element) {
    if (typeof element.flickityInstance !== 'undefined') {
      return await element.flickityInstance;
    }
    return null;
  }

  // Set up color chips.
  setupColorChips(colorChips, flickityElement, flickityInstance, productItem) {
    colorChips.forEach(color => {
      color.addEventListener('click', async () => {
        const selectedUrl = color.getAttribute('data-product-url');
        flickityInstance = await this.filterImages(flickityElement, flickityInstance, selectedUrl);
        this.updateProductInfo(color, productItem);
      });
    });
  }

  // Filter images based on selected URL.
  async filterImages(flickityElement, flickityInstance, selectedUrl) {
    if (!flickityElement || !flickityInstance) {
      return flickityInstance;
    }
    flickityElement.classList.remove('visible');
    const images = Array.from(flickityElement.querySelectorAll('.product-item__color-image'));
    const carousel = flickityElement.querySelector('.flickity-slider');
    if (!carousel) {
      return flickityInstance;
    }
    let hasVisibleImages = false;

    // Remove existing temporary container if it exists
    const existingTempContainer = flickityElement.querySelector('.temp-container');
    if (existingTempContainer) {
      existingTempContainer.remove();
    }

    // Create a new temporary container for filtered images
    const tempContainer = document.createElement('div');
    tempContainer.classList.add('temp-container');
    tempContainer.style.display = 'none';
    flickityElement.appendChild(tempContainer);
    images.forEach(image => {
      const imageUrl = image.getAttribute('data-product-url');
      if (imageUrl.split('?')[0] === selectedUrl.split('?')[0]) {
        carousel.appendChild(image);
        hasVisibleImages = true;
      } else {
        tempContainer.appendChild(image);
      }
    });

    // If no images are visible, put all images back in the carousel
    if (!hasVisibleImages) {
      images.forEach(image => {
        carousel.appendChild(image);
      });
    }
    if (flickityInstance) {
      // Update Flickity
      flickityInstance.reloadCells();
      flickityInstance.resize();
      flickityInstance.select(0, false, true);

      // Force a complete update of Flickity
      flickityInstance.updateDraggable();

      // Update display
      flickityInstance.positionCells();
      flickityInstance.positionSliderAtSelected();

      // Wait for Flickity to settle
      await new Promise(resolve => setTimeout(resolve, 100));

      // Show the carousel
      requestAnimationFrame(() => {
        flickityElement.classList.add('visible');
      });
    }
    return flickityInstance;
  }

  // Update product information.
  updateProductInfo(colorChip, productItem) {
    let title = colorChip.getAttribute('data-product-title');
    productItem.querySelector('.product-item-meta__title').textContent = title;
    let url = colorChip.getAttribute('data-product-url');
    productItem.querySelector('.product-item__aspect-ratio').setAttribute('href', url);
    productItem.querySelector('.product-item-meta__title').setAttribute('href', url);
    let itemId = colorChip.getAttribute('data-item-id');
    const quickFormButton = productItem.querySelector('.product-item__quick-form button');
    if (quickFormButton) {
      quickFormButton.setAttribute('data-cart-add', itemId);
    }
  }

  // Perform initial filtering of images for each product.
  initialFilterImages() {
    this.productItems.forEach(productItem => {
      const flickityElement = productItem.querySelector('flickity-carousel');
      if (flickityElement) {
        const currentVariantUrl = productItem.querySelector('.product-item__aspect-ratio').getAttribute('href');
        const colorChips = productItem.querySelectorAll('.product-item__list-item');
        this.observeFlickityElement(colorChips, flickityElement, productItem, currentVariantUrl);
      }
    });
  }
  initFacetChangeListener() {
    document.addEventListener('facet:rendered', async () => {
      await this.reinitializeFlickity();
      this.showCarousels();
    });
    window.addEventListener('productsLoadedGlobal', async () => {
      await this.reinitializeFlickity();
      this.showCarousels();
    });
  }
  async reinitializeFlickity() {
    const flickityElements = document.querySelectorAll('flickity-carousel');
    await this.waitForDomUpdate();
    this.reset();

    // Hide all carousels initially
    flickityElements.forEach(flickityElement => {
      flickityElement.classList.remove('visible');
    });

    // Wait for all Flickity instances to be fully initialized
    await Promise.all(Array.from(flickityElements).map(async flickityElement => {
      const flickityInstance = await this.getFlickityInstance(flickityElement);
      if (flickityInstance) {
        flickityInstance.reloadCells();
        flickityInstance.resize();
        flickityInstance.select(0, false, true);
        flickityInstance.updateDraggable();
        flickityInstance.positionCells();
        flickityInstance.positionSliderAtSelected();

        // Wait for Flickity to settle
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }));

    // Show all carousels
    requestAnimationFrame(() => {
      flickityElements.forEach(flickityElement => {
        flickityElement.classList.add('visible');
      });
    });
    this.showCarousels();
  }
  waitForDomUpdate() {
    return new Promise(resolve => requestAnimationFrame(resolve));
  }
  reset() {
    this.productItems = Array.from(document.querySelectorAll('.product-item'));
    this.colorSwatchContainers = Array.from(document.querySelectorAll('.product-item__swatch'));

    // Reset Flickity carousels
    this.productItems.forEach(async productItem => {
      const flickityElement = productItem.querySelector('flickity-carousel');
      if (flickityElement) {
        const flickityInstance = await flickityElement.flickityInstance;
        if (flickityInstance) {
          flickityInstance.reloadCells();
          flickityInstance.resize();
          flickityInstance.select(0, false, true);
          flickityInstance.updateDraggable();
          flickityInstance.positionCells();
          flickityInstance.positionSliderAtSelected();
        }
      }
    });
    this.init();
  }
}

// Class representing the color swatch slider functionality.
class ColorSwatchSlider {
  // Create a ColorSwatchSlider.
  constructor(container) {
    this.container = container;
    this.colorSwatch = container.querySelector('.product-item__variant-list');
    this.leftArrow = container.querySelector('.product-item__arrow--left');
    this.rightArrow = container.querySelector('.product-item__arrow--right');
    this.colorChipsCount = this.colorSwatch?.children.length;
    this.colorChipWidth = 20;
    this.click = 0;
    this.isLeftArrowActive = true;
    this.isRightArrowActive = true;
  }

  // Initialize the color swatch slider.
  init() {
    this.addArrowEventListeners();
    this.updateArrows();
  }

  // Add event listeners to arrow buttons.
  addArrowEventListeners() {
    this.leftArrow?.addEventListener('click', () => this.handleLeftArrowClick());
    this.rightArrow?.addEventListener('click', () => this.handleRightArrowClick());
  }

  // Handle left arrow click.
  handleLeftArrowClick() {
    if (this.click <= 0) {
      this.disableArrow('left');
    } else {
      this.click--;
      this.updateSliderPosition();
      this.enableArrow('right');
    }
  }

  // Handle right arrow click.
  handleRightArrowClick() {
    if (this.click >= this.colorChipsCount - 5) {
      this.disableArrow('right');
    } else {
      this.click++;
      this.updateSliderPosition();
      if (this.click >= this.colorChipsCount - 5) {
        this.disableArrow('right');
      }
      this.enableArrow('left');
    }
  }

  // Update slider position.
  updateSliderPosition() {
    this.colorSwatch.style.transform = `translateX(-${this.click * this.colorChipWidth}px)`;
  }

  // Update arrow states.
  updateArrows() {
    if (this.leftArrow) {
      this.leftArrow.style.pointerEvents = this.isLeftArrowActive ? 'all' : 'none';
    }
    if (this.rightArrow) {
      this.rightArrow.style.pointerEvents = this.isRightArrowActive ? 'all' : 'none';
    }
  }

  // Disable an arrow.
  disableArrow(arrow) {
    if (arrow === 'left') {
      this.isLeftArrowActive = false;
    } else if (arrow === 'right') {
      this.isRightArrowActive = false;
    }
    this.updateArrows();
  }

  // Enable an arrow.
  enableArrow(arrow) {
    if (arrow === 'left') {
      this.isLeftArrowActive = true;
    } else if (arrow === 'right') {
      this.isRightArrowActive = true;
    }
    this.updateArrows();
  }
}
let globalColorChipManager;
document.addEventListener('DOMContentLoaded', () => {
  globalColorChipManager = new ColorChipManager();
  globalColorChipManager.init();
});

// Prevent arrow clicks from going to the link in tag a
let sliders = document.querySelectorAll('.product-item__color-carousel');
sliders.forEach(slider => {
  slider.addEventListener('click', event => {
    const isArrow = event.target.closest('.flickity-button');
    if (isArrow) {
      event.preventDefault();
    }
  });
});